<template>
    <box-view class="supplier-module" title="Nhà cung cấp" :showBack="true" @close="$router.push('/admin')">
        <div class="container-fluid py-3">
            <!-- Header -->
            <div class="row mb-4">
                <div class="col-12 d-flex justify-content-between align-items-center">
                    <h5 class="mb-0 text-secondary">
                        <i class="fas fa-truck mr-2"></i>
                        Tổng: <strong class="text-primary">{{ filteredSuppliers.length }}</strong> / {{ suppliers.length }} nhà cung cấp
                    </h5>
                    <button v-if="canManageSuppliers" class="btn btn-primary btn-add" @click="openModal()">
                        <i class="fas fa-plus mr-2"></i> Thêm NCC
                    </button>
                </div>
            </div>

            <!-- Search Bar -->
            <div class="row mb-4">
                <div class="col-12">
                    <div class="search-box">
                        <i class="fas fa-search search-icon"></i>
                        <input 
                            type="text" 
                            class="form-control search-input" 
                            v-model="searchQuery" 
                            placeholder="Tìm kiếm theo tên, số điện thoại hoặc địa chỉ..."
                        />
                        <button v-if="searchQuery" class="btn-clear" @click="searchQuery = ''">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Supplier Cards -->
            <div class="row">
                <div v-for="sup in filteredSuppliers" :key="sup.id" class="col-12 col-md-6 col-lg-4 mb-4">
                    <div class="supplier-card card h-100 border-0 shadow-sm">
                        <div class="card-body p-4">
                            <div class="d-flex align-items-start mb-3">
                                <div class="supplier-icon mr-3">
                                    <i class="fas fa-building"></i>
                                </div>
                                <div class="flex-grow-1">
                                    <h5 class="mb-1 font-weight-bold">{{ sup.name }}</h5>
                                    <div class="text-muted small mb-2">
                                        <i class="fas fa-phone mr-1"></i> {{ sup.phone || 'Chưa có SĐT' }}
                                    </div>
                                    <div v-if="sup.debtAmount > 0" class="text-danger small font-weight-bold">
                                        Còn nợ: {{ sup.debtAmount.toLocaleString() }}₫
                                    </div>
                                </div>
                            </div>

                            <div class="supplier-info mb-3" v-if="sup.address">
                                <i class="fas fa-map-marker-alt text-muted mr-2"></i>
                                <span class="text-truncate">{{ sup.address }}</span>
                            </div>

                            <div v-if="canManageSuppliers" class="supplier-actions d-flex gap-2">
                                <button class="btn btn-sm btn-outline-primary flex-fill" @click="openModal(sup)">
                                    <i class="fas fa-edit mr-1"></i> Sửa
                                </button>
                                <button class="btn btn-sm btn-outline-danger" @click="handleDelete(sup.id)">
                                    <i class="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-if="filteredSuppliers.length === 0 && searchQuery" class="col-12">
                    <div class="empty-state text-center py-5">
                        <i class="fas fa-search fa-4x text-muted mb-3"></i>
                        <h5 class="text-muted">Không tìm thấy kết quả</h5>
                        <p class="text-muted">Thử tìm kiếm với từ khóa khác</p>
                    </div>
                </div>
                <div v-else-if="suppliers.length === 0" class="col-12">
                    <div class="empty-state text-center py-5">
                        <i class="fas fa-truck fa-4x text-muted mb-3"></i>
                        <h5 class="text-muted">Chưa có nhà cung cấp</h5>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal -->
        <van-dialog v-model="showModal" :title="editing ? 'Sửa NCC' : 'Thêm NCC'" show-cancel-button @confirm="save">
            <div class="p-3">
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-building mr-1"></i> Tên</label>
                    <input type="text" class="form-control" v-model="form.name">
                </div>
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-phone mr-1"></i> SĐT</label>
                    <input type="text" class="form-control" v-model="form.phone">
                </div>
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-map-marker-alt mr-1"></i> Địa chỉ</label>
                    <input type="text" class="form-control" v-model="form.address">
                </div>
            </div>
        </van-dialog>
    </box-view>
</template>

<script>
export default {
    name: 'Supplier',
    props: {
        suppliers: {
            type: Array,
            default: () => []
        },
        user: {
            type: Object,
            default: () => ({})
        },
        settings: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            searchQuery: '',
            showModal: false,
            editing: false,
            form: {
                id: null,
                name: '',
                phone: '',
                address: ''
            }
        };
    },
    computed: {
        canManageSuppliers() {
            const role = this.user.role || 'admin';
            if(role === 'admin') return true;
            const roleConfig = this.settings.user?.roles?.[role];
            return roleConfig?.permissions?.includes('manageSuppliers');
        },
        filteredSuppliers() {
            if (!this.searchQuery) return this.suppliers;
            const query = this.searchQuery.toLowerCase().trim();
            return this.suppliers.filter(sup => 
                sup.name.toLowerCase().includes(query) ||
                (sup.phone && sup.phone.toLowerCase().includes(query)) ||
                (sup.address && sup.address.toLowerCase().includes(query))
            );
        }
    },
    methods: {
        openModal(supplier = null) {
            if (supplier) {
                this.editing = true;
                this.form = { ...supplier };
            } else {
                this.editing = false;
                this.form = {
                    id: 'SUP-' + Date.now(),
                    name: '',
                    phone: '',
                    address: ''
                };
            }
            this.showModal = true;
        },
        save() {
            if (!this.form.name) return;
            if (this.editing) {
                this.$emit('update-supplier', this.form);
            } else {
                this.$emit('add-supplier', this.form);
            }
            this.showModal = false;
        },
        handleDelete(id) {
            if (confirm('Xóa nhà cung cấp này?')) {
                this.$emit('delete-supplier', id);
            }
        }
    }
};
</script>

